import classNames from 'classnames';
import type { CSSProperties, FC } from 'react';
import React, { useContext } from 'react';

import { getNormalizedColSpans } from './../../../../../ricos-common-plugins/layout/get-normalized-col-spans.js';
import {
  LAYOUT_DEFAULTS,
  normalizePadding,
} from './../../../../../ricos-common-plugins/layout/shared-with-viewer.js';
import { RicosContext, RicosNodeContextProvider } from './../../../../../ricos-context/index.js';
import type { Node } from './../../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { LayoutNode } from './../../../../../ricos-schema/next-schema';
import type { NodeViewRenderer, NodeViewRendererProps } from './../../../../../ricos-types/index.js';
import { getImageSrc } from './../../../../../wix-rich-content-common/lib/imageUtils.js';
import { LayoutBackgroundLoader } from './../../../../../ui-components/layout-background-loader/layout-background-loader.js';

import styles from './layout-scss.js';

const PROMPT_URL_PREFIX = 'prompt://';

const isGeneratingImage = (media?: { src?: { id?: string; url?: string } }) => {
  const src = media?.src?.id ?? media?.src?.url ?? '';
  return typeof src === 'string' && src.startsWith(PROMPT_URL_PREFIX);
};

const getGridTemplateColumnsTemplate = (node: Node) => {
  const cols: number[] = [];

  node.nodes.forEach(cell => {
    if (cell.type === 'LAYOUT_CELL') {
      cols.push(cell.layoutCellData?.colSpan ?? 0);
    }
  });

  return getNormalizedColSpans(cols)
    .map(col => `minmax(0, ${col}fr)`)
    .join(' ');
};

const getStyleContent = (node: LayoutNode) => {
  // IMPORTANT: do not use " or < in content as it will be encoded in SSR
  const responsivenessBehaviour = {
    STACK: 'stack',
    WRAP: 'wrap',
  }[node.layoutData?.responsivenessBehaviour ?? LAYOUT_DEFAULTS.responsivenessBehaviour];

  const responsivenessBreakpoint =
    node.layoutData?.responsivenessBreakpoint && !isNaN(node.layoutData?.responsivenessBreakpoint)
      ? node.layoutData?.responsivenessBreakpoint
      : LAYOUT_DEFAULTS.responsivenessBreakpoint;

  const scopeValue = encodeURIComponent(
    `layout-${node.id}-${responsivenessBreakpoint}-${responsivenessBehaviour}`
  );
  const isWrapping = responsivenessBehaviour === 'wrap';
  const styleContent = `
    [data-layout-scope=${scopeValue}].${styles.layout} {
      --ricos-internal-layout-display: ${isWrapping ? 'flex' : 'grid'};
      --ricos-internal-layout-justify-content: ${isWrapping ? 'center' : 'auto'};
      --ricos-internal-layout-grid-template: grid-template-columns: minmax(0, 1fr);
      --ricos-internal-layout-cell-min-width: ${
        isWrapping ? `${Math.floor(responsivenessBreakpoint / node.nodes.length)}px` : '100%'
      };
      --ricos-internal-layout-cell-height: ${isWrapping ? 'auto' : '100%'};
    }
  `;

  return {
    scopeValue,
    content: `
@supports not (contain: inline-size) {
  @media only screen and (max-width: ${responsivenessBreakpoint}px) {
    ${styleContent}
  }
}

@container (max-width: ${responsivenessBreakpoint}px) {
  ${styleContent}
}
`,
  };
};

export const Layout: FC<NodeViewRendererProps> = ({ node, nodeIndex, RicosNodesRenderer }) => {
  const { isMobile } = useContext(RicosContext);

  const layoutData = node.type === 'LAYOUT' ? node.layoutData : undefined;
  const backgroundMedia = layoutData?.backgroundImage?.media;
  const backdropMedia = layoutData?.backdropImage?.media;
  const isGeneratingBackground = isGeneratingImage(backgroundMedia);
  const isGeneratingBackdrop = isGeneratingImage(backdropMedia);
  const backgroundImageUrl =
    !isGeneratingBackground &&
    (backgroundMedia?.src?.url ??
      (backgroundMedia?.src?.id &&
        getImageSrc({ file_name: backgroundMedia.src.id }, undefined, {
          requiredQuality: 100,
          requiredWidth: Math.min(backgroundMedia?.width ?? 4000, 4000),
          requiredHeight: Math.min(backgroundMedia?.height ?? 4000, 4000),
        })));
  const backdropImageUrl =
    !isGeneratingBackdrop &&
    (backdropMedia?.src?.url ??
      (backdropMedia?.src?.id &&
        getImageSrc({ file_name: backdropMedia.src.id }, undefined, {
          requiredQuality: 100,
          requiredWidth: Math.min(backdropMedia?.width ?? 4000, 4000),
          requiredHeight: Math.min(backdropMedia?.height ?? 4000, 4000),
        })));

  const shouldShowLoader = isGeneratingBackground || isGeneratingBackdrop;

  const style: CSSProperties = React.useMemo(() => {
    if (node.type !== 'LAYOUT') {
      return {};
    }

    const layoutData = node.layoutData;

    const cellPadding = normalizePadding(layoutData?.cellPadding, [...LAYOUT_DEFAULTS.cellPadding]);
    const cellVerticalAlignment =
      layoutData?.cellVerticalAlignment ?? LAYOUT_DEFAULTS.cellVerticalAlignment;
    const borderColor = layoutData?.borderColor ?? LAYOUT_DEFAULTS.borderColor;
    const borderWidth = layoutData?.borderWidth ?? LAYOUT_DEFAULTS.borderWidth;

    return {
      ...(layoutData?.containerData?.width?.custom
        ? { '--ricos-internal-layout-width': `${layoutData.containerData.width.custom}px` }
        : {}),
      ...(layoutData?.backgroundColor
        ? {
            '--ricos-internal-layout-background-color': layoutData.backgroundColor,
          }
        : {}),
      ...(layoutData?.backgroundImage && backgroundImageUrl
        ? {
            '--ricos-internal-layout-background-image-src': `url(${backgroundImageUrl})`,
            ...(layoutData.backgroundImage.opacity
              ? {
                  '--ricos-internal-layout-background-image-opacity': `${layoutData.backgroundImage.opacity}%`,
                }
              : {}),
            ...(layoutData.backgroundImage.scaling
              ? {
                  '--ricos-internal-layout-background-image-scaling':
                    layoutData.backgroundImage.scaling.toLowerCase(),
                }
              : {}),
            ...(layoutData.backgroundImage.position
              ? {
                  '--ricos-internal-layout-background-image-position':
                    layoutData.backgroundImage.position.toLowerCase().split('_').join(' '),
                }
              : {}),
          }
        : {}),
      ...(borderColor
        ? {
            '--ricos-internal-layout-border-color': borderColor,
            '--ricos-internal-layout-border-width': `${borderWidth}px`,
          }
        : {}),
      ...(layoutData?.borderRadius
        ? {
            '--ricos-internal-layout-border-radius': `${layoutData.borderRadius}px`,
          }
        : {}),
      ...(layoutData?.backdropColor
        ? {
            '--ricos-internal-layout-backdrop-color': layoutData.backdropColor,
          }
        : {}),
      ...(layoutData?.backdropImage && backdropImageUrl
        ? {
            '--ricos-internal-layout-backdrop-image-src': `url(${backdropImageUrl})`,
            ...(layoutData.backdropImage.opacity
              ? {
                  '--ricos-internal-layout-backdrop-image-opacity': `${layoutData.backdropImage.opacity}%`,
                }
              : {}),
            ...(layoutData.backdropImage.scaling
              ? {
                  '--ricos-internal-layout-backdrop-image-scaling':
                    layoutData.backdropImage.scaling.toLowerCase(),
                }
              : {}),
            ...(layoutData.backdropImage.position
              ? {
                  '--ricos-internal-layout-backdrop-image-position':
                    layoutData.backdropImage.position.toLowerCase().split('_').join(' '),
                }
              : {}),
          }
        : {}),
      ...(typeof layoutData?.gap === 'number'
        ? {
            '--ricos-internal-layout-gap': `${layoutData.gap}px`,
          }
        : {}),
      ...(layoutData?.backdropPaddingTop && layoutData.backdropPaddingTop > 0
        ? {
            '--ricos-internal-layout-backdrop-padding-top': `${layoutData.backdropPaddingTop}px`,
          }
        : {}),
      ...(layoutData?.backdropPaddingBottom && layoutData.backdropPaddingBottom > 0
        ? {
            '--ricos-internal-layout-backdrop-padding-bottom': `${layoutData.backdropPaddingBottom}px`,
          }
        : {}),
      ...(cellPadding && cellPadding.length > 0
        ? {
            '--ricos-internal-layout-cell-padding-top': `${cellPadding[0]}px`,
            '--ricos-internal-layout-cell-padding-right': `${cellPadding[1] ?? cellPadding[0]}px`,
            '--ricos-internal-layout-cell-padding-bottom': `${cellPadding[2] ?? cellPadding[0]}px`,
            '--ricos-internal-layout-cell-padding-left': `${
              cellPadding[3] ?? cellPadding[1] ?? cellPadding[0]
            }px`,
          }
        : {}),
      ...(cellVerticalAlignment
        ? {
            '--ricos-internal-layout-cell-vertical-alignment':
              cellVerticalAlignment === 'TOP'
                ? 'flex-start'
                : cellVerticalAlignment === 'BOTTOM'
                  ? 'flex-end'
                  : 'center',
          }
        : {}),
    } as CSSProperties;
  }, [node, backgroundImageUrl, backdropImageUrl]);

  const styleContent = React.useMemo(() => getStyleContent(node as LayoutNode), [node]);

  return (
    <div
      className={styles.layoutWrapper}
      style={style}
      {...(node.layoutData?.banner ? { 'data-layout-banner': 'true' } : {})}
    >
      {shouldShowLoader && (
        <LayoutBackgroundLoader
          backgroundColor={layoutData?.backdropColor ?? layoutData?.backgroundColor ?? undefined}
        />
      )}
      <style data-layout-style={node.id}>{styleContent.content}</style>
      <div data-breakout="normal">
        <div
          data-layout-scope={styleContent.scopeValue}
          className={classNames(styles.layout, {
            [styles.mobile]: isMobile,
            [styles.designOnLayout]:
              !node.layoutData?.designTarget || node.layoutData?.designTarget === 'LAYOUT',
            [styles.designOnCell]: node.layoutData?.designTarget === 'CELL',
          })}
          style={
            {
              '--ricos-internal-layout-column-template': getGridTemplateColumnsTemplate(node),
            } as CSSProperties
          }
        >
          <RicosNodeContextProvider parentNode={node} parentIndex={nodeIndex}>
            <RicosNodesRenderer key={`children-${node.id}`} nodes={node.nodes ?? []} />
          </RicosNodeContextProvider>
        </div>
      </div>
    </div>
  );
};

export const LayoutRenderer: NodeViewRenderer = ({ node, ...props }) => {
  return <Layout key={node.id} node={node} {...props} />;
};
